import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMail,
  FiLock,
  FiEye,
  FiEyeOff,
  FiArrowRight,
  FiShoppingBag,
  FiShield,
  FiTruck,
} from "react-icons/fi";
import { FcGoogle } from "react-icons/fc";
import "./login.css";
import Footer from "../../components/footer/footer";
import Header from "../../components/header/header";

const REACT_APP_API_URL = process.env.REACT_APP_API_URL;

const Login = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [focusedField, setFocusedField] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  // ✅ Handle the redirect from Google (token in URL)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");
    const error = params.get("error");

    if (token) {
      localStorage.setItem("token", token);
      localStorage.removeItem("guestId"); // Remove guest ID after successful login
      // Force a full reload to update Cart Context and Header state
      window.location.href = "/"; 
    }

    if (error) {
      alert("Google Login failed. Please try again.");
      navigate("/login");
    }
  }, [location, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const guestId = localStorage.getItem("guestId");
      const response = await fetch(`${REACT_APP_API_URL}/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password, guestId }),
      });

      const data = await response.json();
      if (!response.ok) {
        alert(data.message || "Login failed");
        return;
      }

      localStorage.setItem("token", data.token);
      localStorage.removeItem("guestId");
      alert("Login successful!");
      window.location.href = "/";
    } catch (error) {
      console.error("Login error:", error);
      alert("Something went wrong");
    }
  };

  // ✅ Redirect to backend Google Auth Route
  const handleGoogleLogin = () => {
    const guestId = localStorage.getItem("guestId") || "";
    // Redirects to backend which then redirects to Google
    window.location.href = `${REACT_APP_API_URL}/auth/google?guestId=${guestId}`;
  };

  // ... (Keep your existing animation variants here) ...
  const containerVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: {
      opacity: 1,
      scale: 1,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1], staggerChildren: 0.08 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] } },
  };

  const brandingVariants = {
    hidden: { opacity: 0, x: -40 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1], staggerChildren: 0.12 },
    },
  };

  const brandItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.5, ease: "easeOut" } },
  };

  const featureVariants = {
    hidden: { opacity: 0, x: -15 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: 0.5 + i * 0.15, duration: 0.4, ease: "easeOut" },
    }),
  };

  return (
    <div>
      <Header />
      <div className="login-page">
        <div className="login-floating-shapes">
          <div className="floating-shape shape-1"></div>
          <div className="floating-shape shape-2"></div>
          <div className="floating-shape shape-3"></div>
          <div className="floating-shape shape-4"></div>
          <div className="floating-shape shape-5"></div>
        </div>

        <motion.div className="login-container" variants={containerVariants} initial="hidden" animate="visible">
          <motion.div className="login-branding" variants={brandingVariants} initial="hidden" animate="visible">
            <div className="branding-content">
              <motion.div className="branding-icon" variants={brandItemVariants}>👤</motion.div>
              <motion.h1 className="branding-title" variants={brandItemVariants}>Crescent Store</motion.h1>
              <motion.p className="branding-subtitle" variants={brandItemVariants}>
                Discover premium products with an experience crafted just for you.
              </motion.p>
              <div className="branding-features">
                {[
                  { icon: <FiShoppingBag />, text: "Curated Collections" },
                  { icon: <FiShield />, text: "Secure Payments" },
                  { icon: <FiTruck />, text: "Fast Delivery" },
                ].map((feature, i) => (
                  <motion.div className="branding-feature" key={i} custom={i} variants={featureVariants} initial="hidden" animate="visible">
                    <span className="feature-icon">{feature.icon}</span>
                    <span>{feature.text}</span>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>

          <motion.div className="login-form-panel" variants={containerVariants} initial="hidden" animate="visible">
            <motion.div className="login-form-header" variants={itemVariants}>
              <h2>Welcome Back</h2>
              <p>Sign in to your account to continue shopping</p>
            </motion.div>

            <motion.button className="google-login-btn" onClick={handleGoogleLogin} variants={itemVariants} whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.98 }}>
              <FcGoogle className="google-icon" />
              Continue with Google
            </motion.button>

            <motion.div className="login-divider" variants={itemVariants}>
              <span>or sign in with email</span>
            </motion.div>

            <form className="login-form" onSubmit={handleSubmit}>
              <motion.div className="input-group-login" variants={itemVariants}>
                <label htmlFor="login-email">Email Address</label>
                <div className="input-wrapper">
                  <motion.span className="input-icon" animate={{ color: focusedField === "email" ? "#6366f1" : "#9ca3af", scale: focusedField === "email" ? 1.15 : 1 }} transition={{ duration: 0.2 }}>
                    <FiMail />
                  </motion.span>
                  <input id="login-email" type="email" placeholder="Enter your email" value={email} onChange={(e) => setEmail(e.target.value)} onFocus={() => setFocusedField("email")} onBlur={() => setFocusedField(null)} required />
                </div>
              </motion.div>

              <motion.div className="input-group-login" variants={itemVariants}>
                <label htmlFor="login-password">Password</label>
                <div className="input-wrapper">
                  <motion.span className="input-icon" animate={{ color: focusedField === "password" ? "#6366f1" : "#9ca3af", scale: focusedField === "password" ? 1.15 : 1 }} transition={{ duration: 0.2 }}>
                    <FiLock />
                  </motion.span>
                  <input id="login-password" type={showPassword ? "text" : "password"} placeholder="Enter your password" value={password} onChange={(e) => setPassword(e.target.value)} onFocus={() => setFocusedField("password")} onBlur={() => setFocusedField(null)} required />
                  <button type="button" className="password-toggle" onClick={() => setShowPassword(!showPassword)} aria-label="Toggle password visibility">
                    <AnimatePresence mode="wait">
                      <motion.span key={showPassword ? "visible" : "hidden"} initial={{ opacity: 0, rotateY: -90 }} animate={{ opacity: 1, rotateY: 0 }} exit={{ opacity: 0, rotateY: 90 }} transition={{ duration: 0.2 }}>
                        {showPassword ? <FiEyeOff /> : <FiEye />}
                      </motion.span>
                    </AnimatePresence>
                  </button>
                </div>
              </motion.div>

              <motion.div className="login-options" variants={itemVariants}>
                <label className="remember-me">
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
                <Link to="/forgot-password" className="forgot-password">Forgot Password?</Link>
              </motion.div>

              <motion.button type="submit" className="login-submit-btn" variants={itemVariants} whileHover={{ scale: 1.015 }} whileTap={{ scale: 0.98 }}>
                <span className="btn-content">
                  Sign In
                  <motion.span animate={{ x: [0, 4, 0] }} transition={{ duration: 1.5, repeat: Infinity, ease: "easeInOut" }}>
                    <FiArrowRight />
                  </motion.span>
                </span>
              </motion.button>
            </form>

            <motion.p className="login-signup" variants={itemVariants}>
              Don't have an account? <Link to="/signup" className="signup-link">Create Account</Link>
            </motion.p>
          </motion.div>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default Login;
